import { AloftDate, SimilarityScoreCharacteristics } from '@aloft/toolkit-shared';

import { CompSelectionProperty } from '../types.ts';

/**
 * Maps a CompSelectionProperty to SimilarityScoreCharacteristics for use with
 * calculateSimilarityScore.
 *
 * @param prop - The property to map.
 * @param effectiveDate - The appraisal effective date, used to compute saleDate
 *   as months elapsed since the comp's sale date. For the subject, pass
 *   `isSubject: true` and saleDate will be set to 0.
 * @param isSubject - When true, saleDate is set to 0 (the subject is treated as
 *   having no elapsed time since the effective date).
 */
export const toSimilarityScoreCharacteristics = (
  prop: CompSelectionProperty,
  effectiveDate: AloftDate,
  isSubject = false,
): SimilarityScoreCharacteristics => {
  const getSaleDate = (): number | undefined => {
    if (isSubject) return 0;
    if (!prop.saleDate) return undefined;
    return (
      (effectiveDate.getYear() - prop.saleDate.getYear()) * 12 +
      (effectiveDate.getMonth() - prop.saleDate.getMonth())
    );
  };

  const saleDate = getSaleDate();

  return {
    gla: prop.gla?.toSqFt().unitAmount,
    proximityMiles: prop.distanceToSubject,
    actualAge: prop.yearBuilt !== undefined ? effectiveDate.getYear() - prop.yearBuilt : undefined,
    quality: prop.quality ?? prop.uadQualityRating,
    condition: prop.condition ?? prop.uadConditionRating,
    lotSize: prop.lotSize?.toSqFt().unitAmount,
    saleDate,
    bedroomCount: prop.bedroomCount,
    fullBathCount: prop.fullBaths,
    halfBathCount: prop.halfBaths,
    garage: prop.garage,
    basement: prop.basement,
    pool: prop.pool,
  };
};
